import react, { useCallback, useEffect, useMemo, useState } from 'react';
import { EllReport, TutorChatStyle } from '@mavatar/melingo-voice-and-avatar-sdk';
import {
  GetChatsPageResponse,
  GetReportResponse,
  GetReportStatisticsResponse,
  GetReportChatsResponse,
} from '@mavatar/melingo-voice-and-avatar-sdk/dist/Tutor/EllReport';
import classNames from './TutorReportModal.module.scss';
import classes from 'classnames';
import { useTranslation } from 'react-i18next';
import SVG from 'react-inlinesvg';
import downArrow from '@/assets/svg/down-arrow.svg';
import upArrow from '@/assets/svg/up-arrow.svg';
import { ClassOverviewData } from '@/types/progress';
import { TeacherGroup } from '@/types/user';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import TutorTopics from '@/utils/tutorTopics';
import History from '@/components/TutorDashboard/History/History';
import { formatDateToStandard } from '@/utils/formatters';
import starIcon from '@/assets/svg/star.svg';
import starEmptyIcon from '@/assets/svg/star-empty.svg';

interface Conversation {
  style: TutorChatStyle;
  date: Date;
  chatId: string;
  analysis: string;
  hasErrors: boolean;
  errors: any[];
  chat: GetReportChatsResponse;
  duration: number;
  rank: number;
  rankReason: string;
}

interface TutorTopicReportProps {
  startHead: any;
  header: any;
  selectedTopic: GetChatsPageResponse;
  selectedStudent: ClassOverviewData;
  chatHistoryId: string | null;
  setChatHistoryId: (chatId: string) => void;
  goBack: () => void;
  group: TeacherGroup;
}

const ConversationRank = ({ rank, className }: { rank: number; className?: string }) => {
  return (
    <div
      className={classes(classNames.conversationRank, {
        [className || '']: !!className,
      })}
    >
      <SVG className={classNames.conversationRankStar} src={rank >= 1 ? starIcon : starEmptyIcon} />
      <SVG className={classNames.conversationRankStar} src={rank >= 2 ? starIcon : starEmptyIcon} />
      <SVG className={classNames.conversationRankStar} src={rank >= 3 ? starIcon : starEmptyIcon} />
    </div>
  );
};

export const getConversationDurationFragments = (durationInSeconds: number) => {
  let time = durationInSeconds;
  const hours = Math.floor(time / 3600);
  time -= hours * 3600;
  const minutes = Math.floor(time / 60);
  time -= minutes * 60;
  const seconds = Math.floor(time);

  return {
    hours,
    minutes,
    seconds,
  };
};

const getConversationDurationStr = (durationInSeconds: number) => {
  const fragments = getConversationDurationFragments(durationInSeconds);

  return fragments.hours
    ? `${fragments.hours}h ${fragments.minutes}m ${fragments.seconds}s`
    : fragments.minutes
      ? `${fragments.minutes}m ${fragments.seconds}s`
      : `${fragments.seconds}s`;
};

const TutorTopicReport = ({
  startHead,
  header,
  selectedTopic,
  selectedStudent,
  chatHistoryId,
  setChatHistoryId,
  goBack,
  group,
}: TutorTopicReportProps) => {
  const { t } = useTranslation();
  const [selectedConversationTutor, setSelectedConversationTutor] = useState<Conversation[] | null>(
    null
  );
  const [expandedConversations, setExpandedConversations] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statistics, setStatistics] = useState<GetReportStatisticsResponse | null>(null);

  useEffect(() => {
    let isActive = true;

    setIsLoading(true);
    setStatistics(null);
    setSelectedConversationTutor([]);
    setExpandedConversations(null);

    const initData = async () => {
      let reportData: GetReportResponse | null = null;

      try {
        const userId = selectedStudent.id;

        reportData = await EllReport.getReport({
          userId: userId,
          levelId: selectedTopic.levelId,
          unitId: selectedTopic.unitId,
          topicId: selectedTopic.topicId,
          activityId: selectedTopic.activityId,
        });
      } catch (ex) {}

      if (!isActive) {
        return;
      }

      if (reportData) {
        setStatistics(reportData.statistics);
        setSelectedConversationTutor(
          (reportData.chats || []).map(chat => {
            let analysis = chat.analysisReport || '';
            if (!group?.id && chat.enhancementReport?.content_summary) {
              analysis = chat.enhancementReport?.content_summary;
            }

            const reportData: Conversation = {
              style: chat.style,
              date: chat.createdDate,
              chatId: chat.chatId,
              chat: chat,
              analysis: analysis,
              hasErrors: false,
              errors: [],
              duration: chat.timeSpentWithTutor,
              rank: chat.rank,
              rankReason: chat.rankReason?.reason || '',
            };

            try {
              const errors = chat.enhancementReport?.enhancement_areas || [];
              errors.forEach(error => {
                reportData.errors.push({
                  type: error.enhancement_area,
                  enhancementAreaSimplified:
                    error.enhancement_area_simplified || error.enhancement_area,
                  description: error.description || '',
                  cefr: error['CEFR level'] || '',
                  count: error.count,
                });
              });
            } catch (ex1) {}

            reportData.hasErrors = reportData.errors.length > 0;

            return reportData;
          })
        );
      }

      setIsLoading(false);
    };

    initData();

    return () => {
      isActive = false;
    };
  }, [group, selectedStudent, selectedTopic]);

  const watchHistory = useCallback((chatId: string) => {
    setChatHistoryId(chatId);
  }, []);

  const toggleConversationDetails = useCallback((index: number) => {
    setExpandedConversations(prevIndex => (prevIndex === index ? null : index));
  }, []);

  const description = useMemo(() => {
    const conversationLevel =
      selectedTopic?.levelId && Number(selectedTopic.levelId)
        ? t(`levelName_${selectedTopic.levelId}`)
        : selectedTopic?.CEFRLevel
          ? t(`levelName_${TutorTopics.TutorChatLevelToEllLevel(selectedTopic.CEFRLevel)}`)
          : '';

    const statisticsList = [
      {
        title: t('my_tutor_page.report.topicOfConversation'),
        value: selectedTopic?.topic,
      },
      {
        title: t('my_tutor_page.report.conversationLevel'),
        value: conversationLevel,
      },
    ];

    return (
      <div className={classNames.statisticsData}>
        {statisticsList.map((statistic, statisticIndex) => {
          return (
            <div key={`statistic--${statisticIndex}`} className={classNames.statisticsDataItem}>
              <div className={classNames.statisticsDataItemTitle}>{statistic.title}</div>
              <div className={classNames.statisticsDataItemVul}>{statistic.value}</div>
            </div>
          );
        })}
      </div>
    );
  }, [t, selectedTopic]);

  const statisticsData = useMemo(() => {
    const totalConversations = statistics?.numberOfConversations || 0;

    let avgMessages = totalConversations ? (statistics?.iterations || 0) / totalConversations : 0;
    avgMessages = Number(avgMessages.toFixed(2));

    let avgMistakes = totalConversations
      ? (statistics?.numberOfMistakes || 0) / totalConversations
      : 0;
    avgMistakes = Number(avgMistakes.toFixed(2));

    const avgConversationDuration = totalConversations
      ? (statistics?.timeSpentWithTutor || 0) / totalConversations
      : 0;
    const avgConversationDurationStr = getConversationDurationStr(avgConversationDuration);

    const statisticsList = [
      {
        title: t('my_tutor_page.report.totalConversations'),
        value: totalConversations,
      },
      {
        title: t('my_tutor_page.report.avgMistakes'),
        value: avgMistakes,
      },
      {
        title: t('my_tutor_page.report.avgConversationDuration'),
        value: avgConversationDurationStr,
      },
    ];

    return (
      <div className={classNames.statisticsData}>
        {statisticsList.map((statistic, statisticIndex) => {
          return (
            <div key={`statistic--${statisticIndex}`} className={classNames.statisticsDataItem}>
              <div className={classNames.statisticsDataItemTitle}>{statistic.title}</div>
              <div className={classNames.statisticsDataItemVul}>{statistic.value}</div>
            </div>
          );
        })}
      </div>
    );
  }, [t, statistics]);

  const ConversationTutorList = useMemo(() => {
    if (!selectedConversationTutor?.length) {
      return null;
    }

    return (
      <div>
        {selectedConversationTutor.map((conversation, index) => {
          const date = conversation.date
            ? // ? `${conversation.date.getDate().toString().padStart(2, '0')}/${(conversation.date.getMonth() + 1).toString().padStart(2, '0')}/${conversation.date.getFullYear()}`
              formatDateToStandard(conversation.date)
            : '';

          return (
            <react.Fragment key={`conversation--${conversation.chatId}`}>
              <button
                onClick={() => toggleConversationDetails(index)}
                className={classNames.conversationButton}
              >
                <div
                  className={classes(classNames.conversationContainer, {
                    [classNames.conversationContainerOpen]: expandedConversations === index,
                  })}
                >
                  <div className={classNames.conversationDetails}>
                    <div className={classNames.conversationTitle}>
                      <span>{date}</span>
                      <div className={classNames.conversationSeparator} />
                      <ConversationRank rank={conversation.rank} />
                    </div>
                    <div
                      className={classNames.conversationDuration}
                    >{`${t('my_tutor_page.report.duration')}: ${getConversationDurationStr(conversation.duration)}`}</div>
                  </div>
                  <SVG
                    className={classNames.arrowIcon}
                    src={expandedConversations === index ? upArrow : downArrow}
                  />
                </div>
              </button>

              <div
                className={classes(classNames.dataTransition, {
                  [classNames.dataTransitionOpen]: expandedConversations === index,
                })}
              >
                <div className={classNames.data}>
                  {conversation.hasErrors && (
                    <div
                      className={classes(classNames.dataItem, {
                        [classNames.dataItemOpen]: expandedConversations === index,
                      })}
                    >
                      {conversation.errors.map((error: any, errorIndex: number) => {
                        return (
                          <div
                            key={`dataItemError--${errorIndex}`}
                            className={classNames.dataItemError}
                          >
                            <div>
                              <div>{group?.id ? error.type : error.enhancementAreaSimplified}</div>
                              <div>
                                <div>
                                  {t('my_tutor_page.report.errorsFound', {
                                    errors: error.count,
                                  })}
                                </div>
                              </div>
                            </div>
                            <div>
                              <div>{t('my_tutor_page.report.description')}:</div>
                              <div className={classNames.ltr}>{error.description}</div>
                            </div>
                            <div>
                              <div>{t('my_tutor_page.report.cefr')}:</div>
                              <div className={classNames.ltr}>{error.cefr}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div
                    className={classes(classNames.dataItem, {
                      [classNames.dataItemOpen]: expandedConversations === index,
                    })}
                  >
                    <div className={classNames.dataItemTitle}>
                      <span>{t('my_tutor_page.report.conversationSummary')}</span>
                      <button
                        onClick={() => {
                          watchHistory(conversation.chatId);
                        }}
                      >
                        {t('my_tutor_page.report.watchHistory')}
                      </button>
                    </div>
                    <div className={classes(classNames.dataItemAnalysis, classNames.ltr)}>
                      {conversation.analysis}
                    </div>
                  </div>
                  <div
                    className={classes(classNames.dataItem, {
                      [classNames.dataItemOpen]: expandedConversations === index,
                    })}
                  >
                    <div className={classNames.dataItemTitle}>
                      <span>{t('my_tutor_page.report.evaluationSummary')}</span>
                      <ConversationRank rank={conversation.rank} />
                    </div>
                    {conversation.rankReason && (
                      <div className={classes(classNames.dataItemAnalysis, classNames.ltr)}>
                        {conversation.rankReason}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </react.Fragment>
          );
        })}
      </div>
    );
  }, [t, selectedConversationTutor, toggleConversationDetails, expandedConversations, group]);

  const history = useMemo(() => {
    if (!chatHistoryId) {
      return null;
    }

    return <History chatId={chatHistoryId} onClose={goBack} />;
  }, [chatHistoryId, goBack]);

  return (
    <>
      {isLoading && (
        <div className={classNames.loader}>
          <AppLoaderCircle />
        </div>
      )}

      {!isLoading && history && (
        <div className={classNames.report}>
          <div className={classNames.head}>{startHead}</div>
          <div className={classNames.body}>{history}</div>
        </div>
      )}

      {!isLoading && !history && (
        <div className={classNames.report}>
          <div className={classNames.head}>
            {startHead}
            {header}
            {description}
            {statisticsData}
          </div>
          <div className={classNames.body}>{ConversationTutorList}</div>
        </div>
      )}
    </>
  );
};

export default TutorTopicReport;
